import React from "react";

import { styled } from "@compiled/react";
import { useIntl } from "react-intl";

import { xcss } from "@atlaskit/primitives-next";
import { token } from "@atlaskit/tokens";

import { GenericErrorDisplay } from "@/common/displays";
import { FastshiftTable } from "@/common/fastshift-table";
import { OrgSelectorDropdown } from "@/common/org-selector-dropdown";
import { useFastshiftSelector, useFastshiftDispatch } from "@/state";
import * as StateActions from "@/state/actions";
import { CustomerDomainOrg } from "@/state/types";

import {
    useMigrationTicketsTable,
    useMigrationTicketsUIAnalytics
} from "../hooks";
import { messages } from "../messages";
import { MigrationTicketsFilters } from "../migration-tickets-filters/migration-tickets-filters";

// Override the default height of the table rows to match the design
/* eslint-disable-next-line local/no-block-disable-of-nested-eslint */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
const StyledTableWrapper = styled.div({
    "table > tbody > tr": {
        height: "48px"
    }
});

const errorDisplayStyles = xcss({
    border: `1px solid ${token("color.border")}`,
    borderRadius: "8px",
    padding: "space.200",
    alignContent: "center",
    height: "412px"
});

export const MigrationTicketsTable: React.FC = () => {
    const {
        isTableLoading,
        isTableError,
        head,
        rows,
        handleRefresh,
        pagination,
        onSetControlsPayload,
        controls: cachedControls,
        filters,
        enableRowClick
    } = useMigrationTicketsTable();

    const intl = useIntl();

    const customerDomain = useFastshiftSelector(
        (state) => state.customerDomain
    );
    const selectedDomainOrg = useFastshiftSelector(
        (state) => state.selectedDomainOrg
    );
    const dispatch = useFastshiftDispatch();

    const {
        sendOrgSelectorDropdownClickUIEvent,
        sendOrgSelectorDropdownItemClickUIEvent
    } = useMigrationTicketsUIAnalytics();

    const handleSetSelectedDomainOrg = (org: CustomerDomainOrg) => {
        if (selectedDomainOrg?.orgId === org.orgId) return;

        dispatch(StateActions.setSelectedDomainOrg(org));
    };

    const errorComponent = (
        <GenericErrorDisplay
            xcss={errorDisplayStyles}
            customMessage={intl.formatMessage(
                messages.migrationTicketsTableErrorMessage
            )}
            onRefresh={handleRefresh}
        />
    );

    const controls = {
        pagination,
        onSetControlsPayload,
        controls: cachedControls
    };

    return (
        <StyledTableWrapper>
            <FastshiftTable
                id="migration-tickets-table"
                enableSearch={true}
                searchAriaLabel={intl.formatMessage(
                    messages.migrationTicketsTableSearchAriaLabel
                )}
                toolbarContent={
                    <>
                        <OrgSelectorDropdown
                            customerDomain={customerDomain}
                            selectedDomainOrg={selectedDomainOrg}
                            onSetSelectedDomainOrg={handleSetSelectedDomainOrg}
                            sendOrgSelectorDropdownClickUIEvent={
                                sendOrgSelectorDropdownClickUIEvent
                            }
                            sendOrgSelectorDropdownItemClickUIEvent={
                                sendOrgSelectorDropdownItemClickUIEvent
                            }
                            buttonSpacing="default"
                        />

                        <MigrationTicketsFilters
                            filters={filters}
                            onSetControlsPayload={onSetControlsPayload}
                            allControls={cachedControls}
                        />
                    </>
                }
                toolbarSpacing="space.400"
                head={head}
                rows={rows}
                isTableLoading={isTableLoading}
                isTableError={isTableError}
                errorComponent={errorComponent}
                controls={controls}
                showToolbarOnError={true}
                enableRowClick={enableRowClick}
            />
        </StyledTableWrapper>
    );
};
